import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import RoleForm from "./RoleForm";
import { useApi } from "../../api/ApiProvider";
import Api from "../../axiosApi/api";
import {
  CreateSecurityRoleCommand,
  ScopeDTO,
  UpdateSecurityRoleCommand,
  UserEntityDetails,
} from "axiosApi/models";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import { ButtonWithOptions } from "components/common/ButtonWithOptions";
import { Save, Edit } from "@mui/icons-material";
import RequiredField from "../common/RequiredField";
import intl from "components/i18n/ReactIntlWrapper";
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
  Grid,
  Typography,
  Box,
  Button,
  MenuItem,
  Card
} from "@mui/material";
import { USER_STATUS } from "../../common/constants";
import Spinner from "../Spinner";
import { handleAPIError } from "common/errorHandler";

export type RoleDetailInput = {
  id?: number | null;
  name?: string | null;
  description?: string | null;
  active?: boolean | null;
  scopes?: ScopeDTO[];
  idUsers?: number[] | null;
  users?: UserEntityDetails[] | null;
  idUserSelected?: number | null;
  userSelected?: any;
};

const RoleDetail = () => {
  const [input, setInput] = useState<RoleDetailInput | null>({
    id: null,
    name: "",
    description: "",
    active: true,
    scopes: null,
    idUsers: null,
    users: null,
    idUserSelected: null,
    userSelected: null,
  });

  const [scopes, setScopes] = useState<{ totalRecords: null | number, queryResult: null | ScopeDTO[]}>(null);

  const api: Api = useApi();

  const navigate = useNavigate();

  const toast = useToastMessageQueue();

  const { id } = useParams();
  const idNumber = parseInt(id);

  const [errors, setErrors] = useState({});

  const [disabled, setDisabled] = useState(false);

  const [disabledNew, setDisabledNew] = useState(false);

  const [loading, setLoading] = useState<boolean>(false);

  const fetchRoleDetail = async (idNumber: number) => {
    setLoading(true);
    const response = api.employeeApi
      .apiVversionSecurityRoleIdGet(idNumber, "1", {})
      .then((response) => {
        if (response.data) {
          const idUsers = response.data?.data?.users?.map((user) => user.id);
          setInput({
            ...input,
            id: idNumber,
            name: response.data?.data?.name,
            description: response.data?.data?.description
              ? response.data?.data?.description
              : "",
            active: response.data?.data?.active,
            scopes: response.data?.data?.scopes,
            users: response.data?.data?.users,
            idUsers: idUsers,
          });
          setLoading(false);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setLoading(false);
      });
  };

  const fetchScopes = async () => {
    setLoading(true);
    const response = await api.employeeApi
      .apiVversionScopeAllGet("1", 1, 200, {})
      .then((response) => {
        if (response.data.data === null) {
          setScopes({
            totalRecords: null,
            queryResult: null,
          });
        } else {
          setScopes({
            totalRecords: response.data?.data?.totalRecords,
            queryResult: response.data?.data?.queryResult,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        if (error.response) {
          handleAPIError(error, toast, errors);
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchScopes();
    if (idNumber) {
      fetchRoleDetail(idNumber);
      setDisabled(true);
    }
    setDisabledNew(true);
  }, [idNumber]);

  const handleInputChange = function (e) {
    setInput({
      ...input,
      [e.target.name]: e.target.value,
    });
    setErrors(
      validate({
        ...input,
        [e.target.name]: e.target.value,
      })
    );
  };

  const validate = function (input: RoleDetailInput) {
    setDisabledNew(false);
    let errors: any = {};
    if (!input.name) {
      errors.name = intl.get("validate.errors.isRequired");
    }
    return errors;
  };

  const handleSubmit = async (saveAndEdit: boolean = false) => {
    setLoading(true);
    const errors = validate(input);
    setErrors(errors);
    if (Object.keys(errors).length === 0) {
      if (idNumber) {
        let cmd: UpdateSecurityRoleCommand = {
          name: input.name,
          description: input.description,
          scopes: input.scopes,
          id: idNumber,
          active: input.active,
        };
        const response = await api.employeeApi
          .apiVversionSecurityRolePut("1", cmd, {})
          .then((response) => {
            toast.success({
              message: intl.get("roleDetail.toast.success.handleSubmit"),
            });
            if (saveAndEdit) {
              setDisabled(true);
            } else {
              navigate("/roles");
            }
            setLoading(false);
          })
          .catch((error) => {
            handleAPIError(error, toast, errors);
            setLoading(false);
          });
      } else {
        let cmd: CreateSecurityRoleCommand = {
          name: input.name,
          description: input.description,
          active: input.active,
          scopes: input.scopes,
        };
        const response = await api.employeeApi
          .apiVversionSecurityRolePost("1", cmd, {})
          .then((response) => {
            toast.success({
              message: intl.get("roleDetail.toast.success.handleSubmit.add"),
            });
            if (saveAndEdit) {
              navigate(`/role/detail/${response.data.data.id}`);
            } else {
              navigate("/roles");
            }
            setLoading(false);
          })
          .catch((error) => {
            handleAPIError(error, toast, errors);
            setLoading(false);
          });
      }
    }
  };

  const handleBackClick = (e) => {
    navigate("/roles");
  };

  const handleSaveClick = () => {
    handleSubmit(true);
  };

  const handleSaveCloseClick = () => {
    handleSubmit(false);
  };

  const handleEditMode = (e) => {
    e.preventDefault();
    setErrors({});
    setDisabled(!disabled);
  };

  const handleStatusChange = (event, value) => {
    const inputVal = {
      ...input,
      active: value,
    };
    setInput({ ...inputVal });
  };

  const handleScopeChange = (event) => {
    setInput((prevInput) => ({
      ...prevInput,
      scopes: event,
    }));
  };

  return (
    <Box className='my-3' sx={{ width: '100%', p: 2 }} >
      <Card sx={{ p: 3 }}>

        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
          {idNumber
            ? intl.get("roleDetail.header")
            : intl.get("newRole.header")}
        </Typography>
        {loading ? (
          <Spinner />
        ) : (
          <RoleForm
            errors={errors}
            input={input}
            handleInputChange={handleInputChange}
            handleStatusChange={handleStatusChange}
            disabled={disabled}
            scopes={scopes?.queryResult}
            idNumber={idNumber}
            users={input.users}
            handleScopeChange={handleScopeChange}
            selectedScopes={input.scopes}
          />
        )}
        <RequiredField />
        <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
          {!idNumber && !disabled && (
            <>
              <Button
                onClick={handleBackClick}
              >
                <KeyboardBackspaceIcon />
              </Button>
              <ButtonWithOptions
                startIcon={<Save />}
                menu={
                  <MenuItem onClick={handleSaveCloseClick}>{intl.get('save&close.button')}</MenuItem>
                }
                disabled={Object.keys(errors).length > 0 || disabledNew}
                className="btn btn-primary"
                onClick={handleSaveClick}
              >
                {intl.get("save.button")}
              </ButtonWithOptions>
            </>
          )}

          {!Number.isNaN(idNumber) && disabled && (
            <>
              <Button
                onClick={handleBackClick}
              >
                <KeyboardBackspaceIcon />
              </Button>
              <Button
                color="primary"
                startIcon={<Edit />}
                onClick={handleEditMode}
              >
                {intl.get("edit.button")}
              </Button>
            </>
          )}

          {!Number.isNaN(idNumber) && !disabled && (
            <>
              <Button
                onClick={handleEditMode}
              >
                {intl.get("cancel.button")}
              </Button>
              <ButtonWithOptions
                startIcon={<Save />}
                menu={
                  <MenuItem onClick={handleSaveCloseClick}>{intl.get('save&close.button')}</MenuItem>
                }
                disabled={Object.keys(errors).length > 0 || disabled}
                className="btn btn-primary"
                onClick={handleSaveClick}
              >
                {intl.get("save.button")}
              </ButtonWithOptions>
            </>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default RoleDetail;
